/**
 * Dashboard widget — shows the chantiers where physical advancement exceeds
 * invoicing. Lets the admin jump straight to the project's finance tab to
 * generate an invoice from the proposal.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Receipt, ArrowRight, Warning, Sparkle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const ReadyToBillWidget = ({ wrapStyle }) => {
  const [data, setData] = useState({ items: [], total_proposed_ht: 0, count_with_ready: 0 });
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/projects/ready-to-bill-summary`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data || { items: [], total_proposed_ht: 0, count_with_ready: 0 }); })
      .catch(() => { if (alive) setData({ items: [], total_proposed_ht: 0, count_with_ready: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data.items || data.items.length === 0) return null;

  const generateDraft = async (projectId) => {
    setGeneratingId(projectId);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/invoice-proposal/draft`,
        {},
        { withCredentials: true }
      );
      toast.success(`Facture brouillon ${res.data.number} creee (${res.data.lines_count} lignes, ${fmt(res.data.total_ht)} EUR HT)`);
      navigate(`/invoicing?tab=facturation&id=${res.data.id}&edit=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur generation brouillon');
    } finally {
      setGeneratingId(null);
    }
  };

  const top = data.items.slice(0, 5);
  return (
    <div style={wrapStyle}>
      <div className="card" data-testid="ready-to-bill-widget">
        <div className="card-header" style={{ backgroundColor: '#FFF8E1' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B45309' }}>
            <Receipt size={20} weight="bold" /> Pret a facturer
            <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: '#B45309', color: 'white', fontSize: '0.72rem' }} data-testid="ready-to-bill-count">
              {data.count_with_ready}
            </span>
          </h3>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: 12, background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A' }}>
            <Warning size={18} color="#B45309" weight="fill" />
            <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>
              <strong style={{ color: '#B45309' }}>{fmt(data.total_proposed_ht)} &euro; HT</strong> d'avancement realise non encore facture sur <strong>{data.count_with_ready}</strong> chantier(s).
            </div>
          </div>
          <table className="data-table" data-testid="ready-to-bill-table" style={{ fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th>Chantier</th>
                <th>Client</th>
                <th style={{ textAlign: 'right' }}>Avancement</th>
                <th style={{ textAlign: 'right' }}>A facturer HT</th>
                <th>Derniere facture</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {top.map((p) => (
                <tr key={p.project_id} data-testid={`ready-to-bill-row-${p.project_id}`}>
                  <td style={{ fontWeight: 600, color: '#002FA7' }}>{p.name || '-'}</td>
                  <td style={{ color: '#5E5F66' }}>{p.client_name || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{p.progress_pct}%</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#B45309' }}>{fmt(p.total_proposed_ht)} &euro;</td>
                  <td style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{p.last_invoice_date || '-'}</td>
                  <td>
                    <div style={{ display: 'inline-flex', gap: 4 }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => generateDraft(p.project_id)}
                        disabled={generatingId === p.project_id}
                        data-testid={`ready-to-bill-draft-${p.project_id}`}
                        title="Generer une facture brouillon a partir de l'avancement"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
                      >
                        <Sparkle size={12} /> {generatingId === p.project_id ? '...' : 'Brouillon'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/invoicing?tab=facturation&projectId=${p.project_id}`)}
                        data-testid={`ready-to-bill-go-${p.project_id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
                      >
                        Ouvrir <ArrowRight size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length > 5 && (
            <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#5E5F66', textAlign: 'right' }}>
              +{data.items.length - 5} autres chantiers concernes
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
